
import React from 'react'
import { Row, Col, Button, Space, Typography, Flex } from 'antd'
import { LeftOutlined } from '@ant-design/icons'
import PropTypes from 'prop-types'
import { useTheme } from 'react-jss'
import { useIsMobile } from '../../hooks'
import { MOBILE_MEDIA_QUERY } from '../../lib/responsive'

const { Text, Title } = Typography

function PageHeader({
  onBack = null,
  subTitle = '',
  title = '',
  footer = null,
  children = null,
  extra = null,
  fullExtraWidth = false,
  // Listings pass the practice name as the subtitle, which the app header
  // already shows. Detail pages use it for the patient name, so this is opt-in
  // rather than a blanket rule.
  hideSubTitleOnMobile = false,
}) {
  const theme = useTheme()
  const isMobile = useIsMobile()
  return (
    <div className="page-heading my-2">
      {/* In one column the icon button costs a whole gutter beside the title,
          so on mobile it becomes a labelled link on its own line above. */}
      {onBack && isMobile && (
        <button type="button" className="page-heading__back" onClick={onBack}>
          <LeftOutlined />
          Back
        </button>
      )}
      <Row align="top" className="mb-1">
        <Col>
          <Space align="start">
            {onBack && !isMobile && (
              <Button
                onClick={onBack}
                icon={<LeftOutlined className="anticon" />}
              />
            )}
            <Flex gap="middle" align="center" justify="flex-end">
              <Title className="title" level={3} >
                {title}
              </Title>
              {subTitle && (
                <Text
                  type="secondary"
                  className={
                    hideSubTitleOnMobile ? 'page-heading__subtitle-hide' : undefined
                  }
                >
                  {subTitle}
                </Text>
              )}
              {footer}
            </Flex>
          </Space>
        </Col>
        <Col
          flex="auto"
          className="page-heading__extra"
          style={{
            width: fullExtraWidth ? '100%' : 'auto',
          }}
        >
          {/* Space is nowrap by default, so a row of action buttons runs off the
              side of a phone and takes the whole page into horizontal scroll. */}
          <Space
            wrap={isMobile}
            style={{ width: fullExtraWidth ? '100%' : 'auto' }}
          >
            {extra}
          </Space>
        </Col>
      </Row>
      <Row>
        {React.Children.map(children, child => (
          <span className="page-actions_meta">{child}</span>
        ))}
      </Row>
      <style>{`
        .page-heading {
          padding: 16px 24px;
          margin-bottom: 23px;
          border: solid 1px ${theme.lightGray};
        }
        .page-heading .title {
          color: ${theme.brandPrimary};
          margin-top: 0;
          margin-bottom: 0;
        }
        .page-actions_meta {
          width: 100%;
        }
        .title {
          flex: 1;
          white-space: nowrap;
        }
        .page-heading__extra {
          text-align: right;
        }
        ${MOBILE_MEDIA_QUERY} {
          /* The bordered box indented its contents by 24px while every control
             below it sat at the container edge, leaving two competing left
             edges. Drop the box on mobile so everything shares one edge. */
          .page-heading {
            padding: 0;
            margin-bottom: 12px;
            border: none;
          }
          .page-heading .title {
            font-size: 20px;
          }
          /* Force the actions onto their own full-width line (the Col carries an
             inline flex from antd, hence !important) and align them with the
             title instead of pushing them to the right edge. Once stacked they
             sit flush against the patient name, hence the small gap. */
          .page-heading__extra {
            text-align: left;
            flex: 0 0 100% !important;
            max-width: 100% !important;
            margin-top: 5px;
          }
          /* Drop the link button's own inset so it sits flush with the title. */
          .page-heading__extra .ant-btn-link {
            padding-left: 0;
          }
          .page-heading__subtitle-hide {
            display: none;
          }
          .page-heading__back {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            margin-bottom: 10px;
            padding: 0;
            border: none;
            background: none;
            font: inherit;
            font-size: 14px;
            line-height: 1;
            color: ${theme.brandPrimary};
            cursor: pointer;
          }
          .page-heading__back .anticon {
            font-size: 12px;
          }
        }
      `}</style>
    </div>
  )
}

export default PageHeader

PageHeader.propTypes = {
  onBack: PropTypes.func,
  subTitle: PropTypes.string,
  title: PropTypes.string,
  footer: PropTypes.node,
  children: PropTypes.node,
  extra: PropTypes.node,
  fullExtraWidth: PropTypes.bool,
  hideSubTitleOnMobile: PropTypes.bool,
}
